import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import InlineForm from '../components/InlineForm';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number, relativeAge } from '../lib/format';
import { ICONS } from '../lib/icons';
import { providerNav } from '../lib/providerNav';

// Compartments: the provider's isolation tree, and the reason the inventory works
// at all. No OCI list API recurses — ListInstances covers exactly one compartment
// — so every inventory read fans out over this list, one call per row.
//
// The page owns the snapshot the same way Region subscriptions owns regions: the
// list is a database read, and *Refresh* is what calls the provider and stores what
// it returns. A compartment created outside this console therefore appears after a
// refresh, and until then its instances are not in any listing.
//
// There is no delete. OCI only accepts one on an empty compartment and processes it
// asynchronously, so a button here would fail more often than it worked.
export default function ProviderCompartments() {
  const { id } = useParams();
  const { setNav } = useSideNav();
  const { isAdmin } = useAuth();

  const providerReq = useAsync(() => api.getProvider(id), [id]);
  const provider = providerReq.data;

  const treeReq = useAsync(() => api.getCompartmentsEnvelope(id), [id]);
  const compartments = useMemo(() => treeReq.data?.data || [], [treeReq.data]);
  const syncedAge = relativeAge(treeReq.data?.meta?.syncedAt);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [created, setCreated] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    if (provider) setNav(providerNav(provider));
  }, [provider, setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  const root = compartments.find((c) => c.root);
  const active = compartments.filter((c) => !c.state || c.state === 'ACTIVE');

  // Refresh means "ask the provider and store the answer", not "re-read the page":
  // a plain reload would show the same snapshot back. Only a superadmin may write
  // it, so everyone else re-reads instead of silently doing nothing.
  const refresh = async () => {
    if (!isAdmin) {
      treeReq.reload();
      return;
    }
    setSyncing(true);
    setSyncError(null);
    setCreated(null);
    try {
      await api.syncCompartments(id);
      treeReq.reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const compartment = await api.createCompartment(id, {
        parentId: form.parentId,
        name: form.name.trim(),
        description: form.description.trim(),
      });
      setCreated(compartment);
      setForm(null);
      // The backend re-syncs after a create, so the tree already has it.
      treeReq.reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (providerReq.loading) return <Loading label="Loading provider…" />;
  if (providerReq.error) {
    return <ErrorState error={providerReq.error} onRetry={providerReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Compartments"
        subtitle={`${provider?.name || id} — the scopes every inventory listing covers`}
        actions={
          <>
            <button
              className="btn btn--ghost"
              onClick={refresh}
              disabled={syncing}
              title={
                isAdmin
                  ? 'Read the compartment tree from the provider and store it'
                  : 'Re-read the stored tree (refreshing it is superadmin only)'
              }
            >
              <Icon name={ICONS.refresh} /> {syncing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              className="btn btn--primary"
              onClick={() => {
                setFormError(null);
                setCreated(null);
                setForm({
                  parentId: root?.id || '',
                  name: '',
                  description: '',
                });
              }}
              disabled={!isAdmin || compartments.length === 0}
              title={
                !isAdmin
                  ? 'Superadmin role required'
                  : compartments.length === 0
                  ? 'Refresh the tree first — a new compartment needs a parent'
                  : undefined
              }
            >
              + Create compartment
            </button>
          </>
        }
      />

      <div className="grid grid--kpi">
        <StatCard
          label="Compartments"
          value={number(compartments.length)}
          hint={syncedAge ? `synced ${syncedAge}` : 'never synced'}
        />
        <StatCard
          label="Listed for inventory"
          value={number(active.length)}
          hint="one API call each"
        />
        <StatCard
          label="Root"
          value={root?.name || '—'}
          hint={root ? 'the configured scope' : 'not synced'}
        />
      </div>

      {syncError ? <div className="form-error">{syncError}</div> : null}

      {created ? (
        <p className="field__hint">
          Created <strong>{created.name}</strong>
          {created.state ? ` (${created.state})` : ''}. OCI provisions a compartment
          asynchronously — it becomes usable once it reads ACTIVE, and it is already
          part of the inventory fan-out.
        </p>
      ) : null}

      <Card
        className="card--quiet-title"
        title={`Compartment tree (${number(compartments.length)})`}
        action={
          <span className="field__hint">
            {syncedAge ? `stored snapshot · synced ${syncedAge}` : 'stored snapshot'}
          </span>
        }
      >
        {form ? (
          <InlineForm
            title="Create compartment"
            onSubmit={submit}
            onCancel={() => {
              setForm(null);
              setFormError(null);
            }}
            submitting={saving}
            submitLabel="Create compartment"
            error={formError}
          >
            <div className="form-grid">
              <label className="field">
                <span>Parent</span>
                <select
                  className="input"
                  value={form.parentId}
                  onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                >
                  {active.map((c) => (
                    <option key={c.id} value={c.id}>
                      {'— '.repeat(c.depth)}
                      {c.name || c.id}
                      {c.root ? ' (root)' : ''}
                    </option>
                  ))}
                </select>
                <span className="field__hint">
                  A compartment can only be created inside an active one.
                </span>
              </label>
              <label className="field">
                <span>Name</span>
                <input
                  className="input"
                  placeholder="e.g. production"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                  required
                />
                <span className="field__hint">
                  No spaces, and unique among its siblings — both are the provider's
                  rules, so a name that breaks them is rejected before the call.
                </span>
              </label>
              <label className="field">
                <span>Description</span>
                <input
                  className="input"
                  placeholder="What lives in this compartment"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  required
                />
                <span className="field__hint">
                  Required by OCI, not by this console.
                </span>
              </label>
            </div>
            <p className="field__hint">
              A new compartment is empty and costs nothing. It widens the inventory
              fan-out by one call per region, and it cannot be deleted from here —
              OCI only deletes an empty compartment, asynchronously.
            </p>
          </InlineForm>
        ) : null}

        {treeReq.loading ? (
          <Loading label="Loading compartments…" />
        ) : treeReq.error ? (
          <ErrorState error={treeReq.error} onRetry={treeReq.reload} />
        ) : compartments.length === 0 ? (
          <EmptyState
            // Nothing seeds this table, and every tenancy has at least a root
            // compartment, so empty can only mean the snapshot was never written.
            title="Not synced yet"
            detail={
              isAdmin
                ? 'Click Refresh to read the compartment tree from the provider and store it. Until then, inventory listings cover only the configured compartment.'
                : 'Ask an administrator to refresh the compartment tree.'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>OCID</th>
                  <th>State</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {compartments.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {/* Depth as indentation: the tree is the point, and a flat
                          list of names says nothing about what contains what. */}
                      <span style={{ paddingLeft: c.depth * 18 }}>
                        <span className="cell-primary">{c.name || '—'}</span>
                        {c.root ? (
                          <span
                            className="badge badge--info"
                            style={{ marginLeft: 8 }}
                          >
                            Root
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="cell-sub mono">
                      <span className="cell-id" title={c.id}>
                        {c.id}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={c.state} />
                    </td>
                    <td className="cell-sub">{c.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="field__hint">
          Every inventory listing covers one compartment per API call, so this list
          decides what the console can see: a compartment missing here is one whose
          instances are missing from Compute. It is a stored snapshot — Refresh asks
          the provider and writes what it returns.
        </p>
      </Card>
    </div>
  );
}
